import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IClub } from "@eagerdog/interfaces";
import { useParams } from "react-router-dom";
import { toast } from "src/components/Toast/ToastManager";

import Breadcrumb from "src/components/Breadcrumb/Breadcrumb";
import TabContent, { ITab, useTabContent } from "src/components/TabContent/TabContent";
import SectionSidebar, { ISectionDefinition, SectionMenuButton, sectionsFromTabs } from "src/components/SectionSidebar/SectionSidebar";

import { helperService } from "src/services/helper.service";
import { apiService } from "src/services";

import MembersTab from "./components/MembersTab/MembersTab";
import StoreTransactionsTab from "./components/StoreTransactionsTab/StoreTransactionsTab";
import DonationsTab from "./components/DonationsTab/DonationsTab";
import CalendarTab from "./components/CalendarTab/CalendarTab";
import AdminPoints from "../Admin/AdminPoints";
import AdminBalances from "../Admin/AdminBalances";
import AdminSettings from "../Admin/AdminSettings";
import { UserSubscriptionTab } from "../Account/UserSubscriptionTab";

import styles from "./KennelClubManage.module.scss";

const CLUB_SECTIONS: ISectionDefinition[] = [
  { name: "Contacts", group: "Club", icon: "registrations" },
  { name: "Calendar", group: "Club", icon: "calendar" },
  { name: "Settings", group: "Club", icon: "settings" },
  { name: "Points", group: "Financials", icon: "incentives" },
  { name: "Balances", group: "Financials", icon: "transactions" },
  { name: "Store Transactions", group: "Financials", icon: "entries" },
  { name: "Donations", group: "Financials", icon: "donations" },
  { name: "Billing", group: "Financials", icon: "transactions" },
];

const KennelClubManage: React.FC = () => {
    const navigate = useNavigate();
    const { clubId, tab } = useParams();
    const { activeTab, setTab } = useTabContent();

    const user = helperService.getUser();
    const [loaded, setLoaded] = useState<boolean>(false);
    const [mobileNavOpen, setMobileNavOpen] = useState<boolean>(false);
    const [club, setClub] = useState<IClub>();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [hasAdmin, setHasAdmin] = useState<boolean>(false);

    const [tabs, setTabs] = useState<ITab[]>([
        { name: "Contacts", actions: [] },
        { name: "Calendar", actions: [] },
        { name: "Points", actions: [] },
        { name: "Balances", actions: [] },
        { name: "Settings", actions: [] },
        { name: "Store Transactions", actions: [] },
        { name: "Donations", actions: [] }
    ]);

    useEffect(() => {
        if (user && club) {
            if (!tabs.some((tab) => tab.name === "Billing")) {
                setTabs(prevTabs => [...prevTabs, { name: "Billing", actions: [] }]);
            }
        }
    }, [user, club]);

    const isAdmin = (id?: string) => {
        if ((club && club.created_by ? club.created_by : id) === user._id) {
        return true;
        }

        return false;
    }

    const setDefaultTab = (tab: string) => {
        setTab(tabs.indexOf(tabs.filter(t => t.name.toLowerCase().replace(" ", "-") === tab.toLowerCase()).length > 0 ? tabs.filter(t => t.name.toLowerCase().replace(" ", "-") === tab.toLowerCase())[0] : tabs[0]));
    }

    const getClub = () => {
        apiService.getClub(clubId as string).then((c) => {
            setClub(c);
            
            if (tab) {
                setDefaultTab(tab);
            }

            setLoaded(true);
            
            if (isAdmin(c.created_by) || (!isAdmin(c.created_by) && helperService.hasAdmin(c._id))) {
                setHasAdmin(true);
            } else {
                toast.show({
                    title: "Kennel Club Manage",
                    content: "Only club admins can manage a kennel club.",
                    duration: 5000,
                    type: "fail"
                });

                navigate("/k/" + clubId);
            }
        });
    }

    useEffect(() => {
    if (!loaded) {
        getClub();
    }
    }, [loaded]);

    // Update active tab when URL parameter changes
    useEffect(() => {
        if (loaded && tab) {
            setDefaultTab(tab);
        }
    }, [tab, loaded]);

    const pageLabel = tabs[activeTab]?.name || "Manage";
    const clubLocation = [club?.address?.city, club?.address?.state, club?.address?.country].filter(Boolean).join(", ");

    const selectTab = (index: number) => {
        setTab(index);
        const name = tabs[index]?.name;
        if (name) {
            window.history.pushState("", "", "/k/" + clubId + "/manage/" + name.toLowerCase().replace(" ", "-"));
        }
    };

    return (
        <div className={styles.KennelClubManage}>
            <SectionSidebar
                storageKey="eagerdog.manageClubSidebarCollapsed"
                menuLabel="Club menu"
                eyebrow={club?.club_name}
                header={{
                    title: club?.club_name || "Club",
                    subtitle: clubLocation,
                    imageUrl: club?.logo_url,
                    imageAlt: club?.club_name || "Club logo",
                    fallbackName: club?.club_name,
                    linkTo: "/k/" + clubId,
                    linkLabel: "View Club",
                }}
                groups={sectionsFromTabs(tabs, CLUB_SECTIONS)}
                activeId={String(activeTab)}
                onSelect={(id) => selectTab(Number(id))}
                mobileOpen={mobileNavOpen}
                onMobileClose={() => setMobileNavOpen(false)}
            />
            <div className={styles.main}>
            <div className={styles.inner}>
                <div className={styles.pageHeader}>
                    <Breadcrumb
                        crumbs={[
                            { label: club?.club_name ? club.club_name : "Club Name", path: "/k/" + clubId },
                            { label: pageLabel }
                        ]}
                    />
                    <SectionMenuButton label="Club menu" onClick={() => setMobileNavOpen(true)} />
                </div>
                <h1>{pageLabel}</h1>
                {club && <TabContent
                    hideTabs
                    setTab={selectTab}
                    activeTab={activeTab}
                    tabs={tabs}>
                    <MembersTab
                        club={club}
                    />
                    <CalendarTab club={club} />
                    <AdminPoints />
                    <AdminBalances />
                    <AdminSettings />
                    <StoreTransactionsTab club={club} />
                    <DonationsTab club={club} />
                    {tabs.some((tab) => tab.name === "Billing") && <UserSubscriptionTab clubId={clubId}/>}
                </TabContent>}
            </div>
            </div>
        </div>
    );
};

export default KennelClubManage;